import React, {
  ChangeEvent,
  ReactNode,
  RefObject,
} from 'react';
import clsx from 'clsx';
import MuiTextarea from '@mui/material/TextareaAutosize';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  VoiceRecognitionData,
} from 'pages/Dashboard/hooks/useSpeechToText';
import type {
  InputValue,
} from 'core/components/formValue';

export type Props = {
  className?: string;
  labelClassName?: string;
  micClassName?: string;
  containerClassName?: string;
  textAreaClassName?: string;
  id: string;
  label?: string | ReactNode;
  value?: InputValue;
  onChange: (val: string) => void;
  onBlur?: () => void;
  onSelect?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  allowSpeechToText?: boolean;
  disabled?: boolean;
  nonEditable?: boolean;
  rows?: number;
  maxRows?: number;
  placeholder?: string;
  readonly?: boolean;
  error?: boolean;
  children?: ReactNode;
  trackingLabel?: string;
  speechToText?: (props: VoiceRecognitionData) => void;
  maxLength?: number;
  inputRef?: RefObject<HTMLTextAreaElement>;
};

export default function Textarea({
  className,
  labelClassName,
  containerClassName,
  textAreaClassName,
  id,
  label,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  nonEditable,
  rows = 5,
  maxRows,
  placeholder,
  readonly = false,
  error,
  children,
  onSelect,
  trackingLabel,
  speechToText,
  maxLength,
  inputRef,
}: Props) {
  let resolvedValue: string;

  if (typeof value === 'string') {
    resolvedValue = value;
  } else if (value == null) {
    resolvedValue = '';
  } else {
    resolvedValue = String(value);
  }

  const textAreaClasses = clsx(
    'populate-textarea',
    textAreaClassName,
    { '!p-0': readonly },
  );
  const controlClasses = clsx(
    'items-start overflow-hidden',
    { 'populate-field-shell': !nonEditable, 'has-error': error },
    containerClassName,
  );

  return (
    <Wrapper
      id={id}
      label={label}
      containerClassName={controlClasses}
      labelClasses={labelClassName}
      className={className}
      required={required}
      allowSpeechToText={false}
      trackingLabel={trackingLabel}
      speechToText={speechToText}
    >
      <MuiTextarea
        className={textAreaClasses}
        id={id}
        placeholder={placeholder}
        value={resolvedValue}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
          onChange(e.target.value);
        }}
        onSelect={onSelect}
        readOnly={readonly}
        minRows={rows}
        tabIndex={-1}
        disabled={disabled}
        maxRows={maxRows}
        onBlur={onBlur}
        maxLength={maxLength}
        ref={inputRef as RefObject<HTMLTextAreaElement>}
      />
      {children}
    </Wrapper>
  );
}
